import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ApiError,
  AppointmentAddon,
  CreateAppointmentAddonInput,
  UpdateAppointmentAddonInput,
} from "../lib/api";

interface AppointmentAddonFormProps {
  addon?: AppointmentAddon;
  onClose: () => void;
  onSuccess: () => void;
}

function AppointmentAddonForm({
  addon,
  onClose,
  onSuccess,
}: AppointmentAddonFormProps) {
  const isEdit = !!addon;
  const [name, setName] = useState(addon?.name ?? "");
  const [description, setDescription] = useState(addon?.description ?? "");
  const [price, setPrice] = useState(addon?.price ?? "");
  const [duration, setDuration] = useState(addon?.duration ?? 15);
  const [isActive, setIsActive] = useState(addon?.isActive ?? true);
  const [error, setError] = useState("");

  const createMutation = useMutation({
    mutationFn: (data: CreateAppointmentAddonInput) =>
      api.appointmentAddons.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to create add-on");
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateAppointmentAddonInput) =>
      api.appointmentAddons.update(addon!.id, data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to update add-on");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Add-on name is required");
      return;
    }

    if (!price.trim()) {
      setError("Price is required");
      return;
    }

    if (duration < 1) {
      setError("Duration must be at least 1 minute");
      return;
    }

    const data: CreateAppointmentAddonInput = {
      name: name.trim(),
      description: description.trim() || null,
      price: price.trim(),
      duration,
      isActive,
    };

    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          {isEdit ? "Edit Add-on" : "Add New Add-on"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="addonName"
              className="block text-sm font-medium text-gray-700"
            >
              Add-on name
            </label>
            <input
              id="addonName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>

          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700"
            >
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="price"
                className="block text-sm font-medium text-gray-700"
              >
                Price
              </label>
              <div className="mt-1 relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                  $
                </span>
                <input
                  id="price"
                  type="text"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="block w-full rounded-md border border-gray-300 pl-7 pr-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="duration"
                className="block text-sm font-medium text-gray-700"
              >
                Duration (minutes)
              </label>
              <input
                id="duration"
                type="number"
                min="1"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value) || 1)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                required
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              id="isActive"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
            />
            <label
              htmlFor="isActive"
              className="ml-2 block text-sm text-gray-700"
            >
              Active (available for booking)
            </label>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending
                ? "Saving..."
                : isEdit
                  ? "Save Changes"
                  : "Create Add-on"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeleteConfirmModalProps {
  addon: AppointmentAddon;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  error?: string;
}

function DeleteConfirmModal({
  addon,
  onClose,
  onConfirm,
  isPending,
  error,
}: DeleteConfirmModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Delete Add-on?</h2>
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete &quot;{addon.name}&quot;? This action
          cannot be undone.
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatPrice(price: string): string {
  const num = parseFloat(price);
  if (isNaN(num)) return price;
  return `$${num.toFixed(2)}`;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

function AppointmentAddonRow({
  addon,
  onEdit,
  onDelete,
}: {
  addon: AppointmentAddon;
  onEdit: (addon: AppointmentAddon) => void;
  onDelete: (addon: AppointmentAddon) => void;
}) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="font-medium text-gray-900">{addon.name}</span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatPrice(addon.price)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDuration(addon.duration)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        {addon.isActive ? (
          <span className="text-green-600">Active</span>
        ) : (
          <span className="text-gray-400">Inactive</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
        <button
          onClick={() => onEdit(addon)}
          className="text-amber-600 hover:text-amber-700 mr-4"
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(addon)}
          className="text-red-600 hover:text-red-700"
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

export function AppointmentAddonsPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAddon, setEditingAddon] = useState<AppointmentAddon | null>(
    null
  );
  const [deletingAddon, setDeletingAddon] = useState<AppointmentAddon | null>(
    null
  );
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["appointmentAddons"],
    queryFn: () => api.appointmentAddons.list(),
  });

  const [deleteError, setDeleteError] = useState("");

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.appointmentAddons.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appointmentAddons"] });
      setDeletingAddon(null);
      setDeleteError("");
    },
    onError: (err) => {
      setDeleteError(
        err instanceof ApiError ? err.message : "Failed to delete add-on"
      );
    },
  });

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["appointmentAddons"] });
  };

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">
            {error instanceof ApiError
              ? error.message
              : "Failed to load add-ons"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Appointment Add-ons</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
        >
          Add Add-on
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-pulse text-gray-500">Loading add-ons...</div>
        </div>
      ) : data?.data.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">
            No add-ons yet. Add your first add-on to get started.
          </p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.data.map((addon) => (
                <AppointmentAddonRow
                  key={addon.id}
                  addon={addon}
                  onEdit={setEditingAddon}
                  onDelete={setDeletingAddon}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <AppointmentAddonForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleSuccess}
        />
      )}

      {editingAddon && (
        <AppointmentAddonForm
          addon={editingAddon}
          onClose={() => setEditingAddon(null)}
          onSuccess={handleSuccess}
        />
      )}

      {deletingAddon && (
        <DeleteConfirmModal
          addon={deletingAddon}
          onClose={() => {
            setDeletingAddon(null);
            setDeleteError("");
          }}
          onConfirm={() => deleteMutation.mutate(deletingAddon.id)}
          isPending={deleteMutation.isPending}
          error={deleteError}
        />
      )}
    </div>
  );
}
